import type {
  ActivityUser,
  CollectionActivityItem,
  CollectionListingEvent,
  CollectionOfferEvent,
  CollectionTransferEvent,
} from "./types";

/**
 * A COLLECTION'S ACTIVITY, ONE LINE AT A TIME — `GET /collections/{contract}/activity` is no
 * longer only transfers: each row says what it is in `type` (`activity` a transfer, `offer`,
 * `listing`) and, for the market's two, how it went in `status`. Every reading of a row is
 * here, pure, so the tab only draws (`activity-events.test.ts`).
 */

const ZERO_ADDRESS = /^0x0{40}$/i;

export type EventKind =
  | "mint" | "transfer"
  | "offer" | "offer-accepted" | "offer-sold" | "offer-cancelled" | "offer-failed"
  | "listing" | "listing-cancelled" | "listing-failed";

export const isOfferEvent = (e: CollectionActivityItem): e is CollectionOfferEvent => e.type === "offer";
export const isListingEvent = (e: CollectionActivityItem): e is CollectionListingEvent => e.type === "listing";
/** Anything that is not an offer or a listing is read as a transfer — the endpoint's original row. */
export const isTransferEvent = (e: CollectionActivityItem): e is CollectionTransferEvent => !isOfferEvent(e) && !isListingEvent(e);

export function eventKind(e: CollectionActivityItem): EventKind {
  if (isOfferEvent(e)) {
    switch (e.status) {
      case "accepted": return "offer-accepted";
      case "success": return "offer-sold";
      case "cancelled": case "canceled": return "offer-cancelled";
      case "fail": case "failed": return "offer-failed";
      default: return "offer";
    }
  }
  if (isListingEvent(e)) {
    switch (e.status) {
      case "cancelled": case "canceled": return "listing-cancelled";
      case "fail": case "failed": return "listing-failed";
      default: return "listing";
    }
  }
  const from = e.from_address?.address?.trim() ?? "";
  return !from || ZERO_ADDRESS.test(from) ? "mint" : "transfer";
}

/** The word for each kind, in both languages. */
export const EVENT_WORDS: Record<EventKind, { fa: string; en: string }> = {
  mint: { fa: "ساخت", en: "Mint" },
  transfer: { fa: "انتقال", en: "Transfer" },
  offer: { fa: "پیشنهاد", en: "Offer" },
  "offer-accepted": { fa: "پیشنهاد پذیرفته شد", en: "Offer accepted" },
  "offer-sold": { fa: "فروش با پیشنهاد", en: "Sold via offer" },
  "offer-cancelled": { fa: "پیشنهاد لغو شد", en: "Offer cancelled" },
  "offer-failed": { fa: "پیشنهاد ناموفق", en: "Offer failed" },
  listing: { fa: "لیست فروش", en: "Listed" },
  "listing-cancelled": { fa: "لیست لغو شد", en: "Listing cancelled" },
  "listing-failed": { fa: "لیست ناموفق", en: "Listing failed" },
};

/** The tone a kind is drawn in: the rail's mark and the word. */
export function eventTone(kind: EventKind): "gold" | "accent" | "offer" | "sale" | "muted" {
  if (kind === "mint") return "gold";
  if (kind === "transfer" || kind === "listing") return "accent";
  if (kind === "offer") return "offer";
  if (kind === "offer-accepted" || kind === "offer-sold") return "sale";
  return "muted";
}

/**
 * Stable identity of one row. A transfer is its (tx, log) — as it always was; a market record
 * its type and id. A row with neither (a transfer recorded without a transaction, `"-"`) falls
 * back on what it does carry.
 */
export function activityKey(e: CollectionActivityItem): string {
  if (isOfferEvent(e)) return `offer-${e.id ?? e.order_hash ?? e.tx_hash ?? ""}-${e.token_id}-${e.timestamp}`;
  if (isListingEvent(e)) return `listing-${e.id}`;
  const tx = e.tx_hash && e.tx_hash !== "-" ? e.tx_hash : null;
  if (tx) return `${tx}-${e.log_index}`;
  return `transfer-${e.token_id ?? ""}-${e.to_address?.address ?? ""}-${e.timestamp}`;
}

/**
 * A market row's amount and its currency: `price` in `currency` when the row names one; a
 * record from another market has only `price_eth` (ethereum) or `price_pol` (polygon).
 * A token's own symbol is upper-cased (`pol` → POL).
 */
export function eventPrice(e: CollectionOfferEvent | CollectionListingEvent): { amount: string; symbol: string } | null {
  const symbol = e.currency?.symbol?.trim();
  if (symbol && e.price != null && e.price !== "") return { amount: e.price, symbol: symbol.toUpperCase() };
  const eth = "price_eth" in e ? e.price_eth : null;
  if (eth) return { amount: eth, symbol: "ETH" };
  if (e.price_pol) return { amount: e.price_pol, symbol: "POL" };
  return null;
}

/** The account a market row names, or null on a record from another market (`user: []`). */
export function eventUser(user: ActivityUser | undefined): { name: string; username: string | null; avatar: string | null } | null {
  if (!user || Array.isArray(user)) return null;
  const u = user as { name?: string | null; username?: string | null; avatar?: string | null };
  const name = u.name?.trim() || u.username?.trim();
  return name ? { name, username: u.username?.trim() || null, avatar: u.avatar ?? null } : null;
}

/** The token a row is about — a market row may carry it as a number inside `nft`. */
export function eventTokenId(e: CollectionActivityItem): string | null {
  if (isTransferEvent(e)) return e.token_id != null && e.token_id !== "" ? String(e.token_id) : null;
  const id = e.nft?.token_id ?? e.token_id;
  return id != null && id !== "" ? String(id) : null;
}
